import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { RootState } from '@modules/index';
import {
  RegisterRfqInput,
  CapaRfqPcbMaterialSupplyMethodType
} from '@generated/graphql';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestInputCommonProps } from './types';

// CapaRfqPcbMaterialSupplyMethodType.CustomerSupply : 사급
// CapaRfqPcbMaterialSupplyMethodType.SelfSupply : 도급
// CapaRfqPcbMaterialSupplyMethodType.PartialSelfSupply : 부분도급

const AssemblyMaterialSupplyMethodControl: React.FC<RequestInputCommonProps> = ({
  error
}) => {
  const materialSupplyMethodDefaultValue = useSelector<RootState>(
    (state) => state.request.pcb?.materialSupplyMethod
  ) as CapaRfqPcbMaterialSupplyMethodType;
  const { control } = useFormContext<RegisterRfqInput>();
  const { controlContainer } = useStyles();
  const [materialSupplyMethod, setMaterialSupplyMethod] = useState<
    CapaRfqPcbMaterialSupplyMethodType
  >(
    materialSupplyMethodDefaultValue ??
      CapaRfqPcbMaterialSupplyMethodType.CustomerSupply
  );

  return (
    <>
      <FormInputLayout label="자재" required={true}>
        <Controller
          data-cy="rfq-pcb-materialSupplyMethod"
          name="pcb.materialSupplyMethod"
          control={control}
          render={(renderProps) => (
            <div className={controlContainer}>
              <div
                className={clsx([
                  'switch',
                  {
                    active:
                      materialSupplyMethod ===
                      CapaRfqPcbMaterialSupplyMethodType.CustomerSupply
                  }
                ])}
                onClick={(e) => {
                  setMaterialSupplyMethod(
                    CapaRfqPcbMaterialSupplyMethodType.CustomerSupply
                  );
                  renderProps.onChange(
                    CapaRfqPcbMaterialSupplyMethodType.CustomerSupply
                  );
                }}
              >
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  사급
                </CustomizedTypography>
              </div>
              <div
                className={clsx([
                  'switch',
                  {
                    active:
                      materialSupplyMethod ===
                      CapaRfqPcbMaterialSupplyMethodType.SelfSupply
                  }
                ])}
                onClick={(e) => {
                  setMaterialSupplyMethod(
                    CapaRfqPcbMaterialSupplyMethodType.SelfSupply
                  );
                  renderProps.onChange(
                    CapaRfqPcbMaterialSupplyMethodType.SelfSupply
                  );
                }}
              >
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  도급
                </CustomizedTypography>
              </div>
              <div
                className={clsx([
                  'switch',
                  {
                    active:
                      materialSupplyMethod ===
                      CapaRfqPcbMaterialSupplyMethodType.PartialSelfSupply
                  }
                ])}
                onClick={(e) => {
                  setMaterialSupplyMethod(
                    CapaRfqPcbMaterialSupplyMethodType.PartialSelfSupply
                  );
                  renderProps.onChange(
                    CapaRfqPcbMaterialSupplyMethodType.PartialSelfSupply
                  );
                }}
              >
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  부분도급
                </CustomizedTypography>
              </div>
            </div>
          )}
        />

        {error && (
          <CustomizedTypography variant="caption" color="error">
            {error}
          </CustomizedTypography>
        )}
      </FormInputLayout>
    </>
  );
};

export default AssemblyMaterialSupplyMethodControl;

const useStyles = makeStyles((theme) => ({
  controlContainer: {
    display: 'flex',
    gap: '16px',
    '& .switch': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 18px',
      borderRadius: '12px',
      border: `1px solid ${colorSet.gray400}`,
      gap: '12px',
      backgroundColor: colorSet.gray000,
      '& img': {
        marginBottom: '12px'
      },
      '&.active': {
        border: `2px solid ${colorSet.gray900}`,
        padding: '19px 17px',
        backgroundColor: colorSet.gray100
      },
      [theme.breakpoints.up('md')]: {
        '&:hover': {
          cursor: 'pointer',
          border: `2px solid ${colorSet.gray900}`,
          padding: '19px 17px'
        }
      }
    }
  }
}));
